import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { FaPlay, FaTimes } from 'react-icons/fa';
import { GrCurrency } from 'react-icons/gr';
import DeviceTypeIcon from '../device-type-icon';
import { Platform } from '../../enums/devices';
import { sendWebEvent } from 'api/user';
import { adjustOSName } from 'helpers';
import RulesAndTips from '../rules-and-tips';
import IosModal from '../ios-modal';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

const Cta: React.FC<{
  price: string;
  platform: Platform;
  image: string;
  title: string;
  description: string;
  qrCodeComponent: React.ReactNode;
  link?: string;
}> = ({ price, platform, image, title, description, qrCodeComponent, link }) => {
  const [showQRModal, setShowQRModal] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);

  // Helper functions for local storage
  const getModalCount = (modalType: 'rules' | 'ios'): number => {
    try {
      const count = localStorage.getItem(`${modalType}ModalCount`);
      return count ? parseInt(count, 10) : 0;
    } catch {
      return 0;
    }
  };

  const incrementModalCount = (modalType: 'rules' | 'ios'): void => {
    try {
      const currentCount = getModalCount(modalType);
      localStorage.setItem(`${modalType}ModalCount`, (currentCount + 1).toString());
    } catch {
      // Fail silently if localStorage is not available
    }
  };

  const shouldShowModal = (modalType: 'rules' | 'ios'): boolean => {
    return getModalCount(modalType) < 3;
  };

  const handlePlayToWinClick = () => {
    if (shouldShowModal('rules')) {
      setShowRules(true);
      incrementModalCount('rules');
    } else {
      // Skip rules modal and go directly to handleRulesContinue logic
      handleRulesContinue();
    }
  };

  const handleOpenQRorClickout = () => {
    // If QR component's not provided, means android or iOS, so open link to store directly
    if (!qrCodeComponent) {
      window.open(link, '_blank')?.focus();
    }    // Otherwise, means desktop (or Other), so open the QR modal
    else{
      setShowQRModal(true);
    }
    // Push event to dataLayer
    // @ts-ignore
    window.dataLayer?.push({
      event: 'open_offer',
    });
    // Send web event for opening offer
    void sendWebEvent('open_offer');
    // Fire Firebase Analytics event
    void FirebaseAnalytics.logEvent({ 
      name: 'open_offer',
      params: {
        platform: platform,
        offer_title: title,
        price: price
      }
    });
  }
  
  const handleRulesContinue = () => {
    // Close the rules modal, since continuing
    setShowRules(false);
    
    // Open iOS tracking modal if platform's offer is iOS and exit early
    if (platform === 'ios' ) {
      if (shouldShowModal('ios')) {
        // Open the iOS tracking modal
        setShowIosModal(true);
        incrementModalCount('ios');
      } else {
        // Skip iOS modal and go directly to handleIosModalContinue logic
        handleIosModalContinue();
      }
      // Exit early to prevent opening the QR Image and/ or sending events
      return;
    }

    // Open the QR code image or store link directly
    handleOpenQRorClickout();
  };
  
  const handleIosModalContinue = () => {
    // Close the iOS modal, since continuing
    setShowIosModal(false);

    // Open the QR code image or store link directly
    handleOpenQRorClickout();
  };

  // Stop propagation to prevent clicks from reaching backdrop
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      {/* Main Play to Win button */}
      <div className={classNames(styles.cta)} onClick={handlePlayToWinClick}>
        <FaPlay /> Play to Earn {price}
      </div>

      {/* Rules and Tips modal */}
      <RulesAndTips
        isOpen={showRules} 
        onClose={() => setShowRules(false)} 
        onContinue={handleRulesContinue} 
      />

      {/* iOS Modal */}
      <IosModal
        isOpen={showIosModal} 
        onClose={() => setShowIosModal(false)} 
        onContinue={handleIosModalContinue}
        appName={title} // Pass the title as the app name
      />
      
      {/* Custom QR Code Modal */}
      {showQRModal && (
        <div className={styles.qrModalBackdrop} onClick={() => setShowQRModal(false)}>
          <div className={styles.qrModalContent} onClick={handleContentClick}>
             <div className={styles.top}>
              <div onClick={() => setShowQRModal(false)} className={styles.closeButton}>
                <FaTimes />
              </div>
            </div>

            <div className={styles.title}>Scan QR with your phone's camera:</div>
            <div className={styles.qrWrapper}>
              <div className={styles.qrCodeComponent}>
                {qrCodeComponent}
              </div>
            </div>

            <div className={styles.devices}>
              <div className={styles.subtitle}>Available for {adjustOSName(platform)}</div>
              <div className={styles.deviceIcon}>
                <DeviceTypeIcon deviceType={platform} />
              </div>
            </div>

            <div className={styles.footer}>
              <div className={styles.left}>
                <div className={styles.itemImage}>
                  <img src={image} alt={title} />
                </div>
                <div className={styles.w100}>
                  <div className={styles.subtitle}>{title}</div>
                  <div className={styles.text}>{description}</div>
                </div>
              </div>
              <div className={styles.tag}>
                <GrCurrency />{price}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Cta;